import { ApolloLink, Observable, split } from '@apollo/client/core/index.js'
import { SetContextLink } from '@apollo/client/link/context'
import { RetryLink } from '@apollo/client/link/retry'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import WebSocket from 'isomorphic-ws'
import { getMainDefinition } from '@apollo/client/utilities'
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs'

import { $RootStore, platformOptions } from '../../src/stores/RootStore'
import { $CommunicationConfig } from './Communication'
import { runInAction } from 'mobx'
import { AUTH_ERROR_CODES, buildError } from './ErrorHelper'
import type SubscriptionDebug from './SubscriptionDebug'

const fileUploadResolvers = ['uploadFile', 'uploadSignature', 'uploadConsultationImages']

const getSubscriptionOperationName = (operation: ApolloLink.Operation) => {
  if (operation.operationName) return operation.operationName

  const definition = getMainDefinition(operation.query)
  if (definition.kind !== 'OperationDefinition') return 'subscription'

  const selection = definition.selectionSet.selections[0]
  if (selection?.kind === 'Field') return selection.name.value

  return 'subscription'
}

const createClientLinks = (
  store: $RootStore,
  config: $CommunicationConfig,
  subscriptionDebug?: SubscriptionDebug,
) => {
  const uploadLink: ApolloLink = new UploadHttpLink({
    uri: config.graphUrl,
    fetch,
    credentials: 'include',
    ...platformOptions,
  }) as any

  const authLink = new SetContextLink(async ({ headers }, { operationName }) => {
    if (!operationName) return

    const authToken = await store.authStore?.getAccessAuthToken(operationName)

    return {
      headers: {
        ...headers,
        authorization: authToken?.auth ? `Bearer ${authToken.auth}` : '',
        locallyexpiredtoken: !!authToken?.locallyexpiredtoken,
        timezone: config.timezone,
        platform: config.platform,
        deviceip: config.deviceip,
      },
    }
  })

  // Retry failed requests
  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: (error, operation) => {
        const built = buildError(error, operation.operationName || '', operation.variables || {})

        if (AUTH_ERROR_CODES.includes(built.code as any)) return false

        if (built.code === 'SERVER_CONNECTION_ERROR' && built.message?.includes('auth header')) return false

        return !!error
      },
    },
  })

  const cleanTypeName = new ApolloLink((operation, forward) => {
    if (operation.variables && !fileUploadResolvers.includes(operation.operationName || '')) {
      const omitTypename = (key: string | undefined, value: any) => (key === '__typename' ? undefined : value)
      operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename)
    }

    return forward(operation)
  })

  const trackingLink = new ApolloLink((operation, forward) => {
    const opDefinition = getMainDefinition(operation.query)
    if (
      opDefinition.kind === 'OperationDefinition' &&
      opDefinition.operation === 'subscription'
    ) {
      const subscriptionName = getSubscriptionOperationName(operation)
      const forwardOp = forward(operation)

      if (!forwardOp || typeof (forwardOp as { subscribe?: unknown }).subscribe !== 'function') {
        return forwardOp
      }

      return new Observable(subscriber => {
        subscriptionDebug?.logEvent({
          kind: 'subscription',
          name: subscriptionName,
          detail: 'subscribe',
        })

        const innerSub = forwardOp.subscribe({
          next: value => {
            subscriptionDebug?.logEvent({
              kind: 'subscription',
              name: subscriptionName,
              detail: 'next',
              payload: value,
            })
            subscriber.next(value)
          },
          error: error => {
            subscriptionDebug?.logEvent({
              kind: 'error',
              name: subscriptionName,
              detail: error instanceof Error ? error.message : String(error),
            })
            subscriber.error(error)
          },
          complete: () => {
            subscriptionDebug?.logEvent({
              kind: 'subscription',
              name: subscriptionName,
              detail: 'complete',
            })
            subscriber.complete()
          },
        })

        return () => innerSub.unsubscribe()
      })
    }

    const id = operation.operationName + '-' + Date.now()
    const forwardOp = forward(operation)

    if (!forwardOp || typeof (forwardOp as { subscribe?: unknown }).subscribe !== 'function') {
      return forwardOp
    }

    return new Observable(subscriber => {
      runInAction(() => {
        store.uiStore.activeRequests.push({
          ...operation,
          id,
          operationName: operation.operationName as keyof Query | keyof Mutation,
        })
      })

      const innerSub = forwardOp.subscribe({
        next: value => subscriber.next(value),
        error: error => subscriber.error(error),
        complete: () => subscriber.complete(),
      })

      return () => {
        innerSub.unsubscribe()
        runInAction(() => {
          const index = store.uiStore.activeRequests.findIndex(request => request.id === id)
          if (index !== -1) {
            store.uiStore.activeRequests.splice(index, 1)
          }
        })
      }
    })
  })

  if (config.wsUrl) {
    subscriptionDebug?.setWsUrl(config.wsUrl)

    const wsClient = createClient({
      url: config.wsUrl,
      webSocketImpl: WebSocket,
      lazy: true,
      retryAttempts: Infinity,
      connectionParams: async () => {
        const authToken = await store.authStore?.getAccessAuthToken('subscription')

        return {
          headers: {
            authorization: authToken?.auth ? `Bearer ${authToken.auth}` : '',
            platform: config.platform,
            timezone: config.timezone,
            deviceip: config.deviceip,
          },
        }
      },
    })

    const wsLink = new GraphQLWsLink(wsClient)

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query)
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        )
      },
      wsLink,
      uploadLink as any,
    )

    return {
      links: [
        cleanTypeName,
        retryLink,
        authLink,
        trackingLink,
        splitLink,
      ],
      wsLink,
      wsClient,
    }
  }

  return {
    links: [
      cleanTypeName,
      retryLink,
      authLink,
      trackingLink,
      uploadLink,
    ],
  }
}

export default createClientLinks
